import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  PlusCircle,
  Search,
  X,
  CheckCircle2,
  Ship,
  Calendar,
  Building2,
  ShieldAlert,
  Copy,
  RefreshCw,
  Hash,
  Mail,
  Loader2,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import LclRequestForm from './components/LclRequestForm';
import LclRequestDetailDialog from './components/LclRequestDetailDialog';
import LclInstruccionesEmbarqueDialog from './instrucciones/components/LclInstruccionesEmbarqueDialog.jsx';
import LclTraceabilitySection from './components/LclTraceabilitySection';
import LclOperationKanban from './components/operations/LclOperationKanban';
import { useToast } from '@/components/ui/toaster.jsx';
import { useAuth } from '@/contexts/AuthContext.jsx';
import {
  fetchLclRequests,
  createLclRequest,
  deleteLclRequest,
  setEstado,
} from './Services/lclRequestsService.js';
import { notifyRequestCreated } from '@/pages/traceability/Services/traceabilityService';
import { shortId, formatCliente, formatRuta } from './utils/lclFormatters.js';
import { ESTADOS, BOARD_ROUTE_BY_ESTADO } from './utils/lclBoardConstants.js';

/**
 * Página "Solicitudes LCL" — tablero Kanban con 4 columnas:
 *  - Cotizaciones (Borrador)        → /lcl/board/cotizaciones
 *  - Operaciones en curso (Enviada) → /lcl/board/en-curso
 *  - Finalizadas (Aprobada)         → /lcl/board/finalizadas
 *  - Rechazadas (Rechazada)         → /lcl/board/rechazadas
 *
 * Click en "Ver tablero" del header de una columna → abre el tablero
 * detallado. Click en una tarjeta → abre el detalle de la solicitud.
 */
export default function LclRequestsPage() {
  const toast = useToast();
  const { tenant } = useAuth();

  // ============= Estado =============
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openNewModal, setOpenNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detailRequest, setDetailRequest] = useState(null);
  const [instruccionesRequest, setInstruccionesRequest] = useState(null);

  // Modal de éxito al crear: muestra el número de solicitud y el de
  // trazabilidad generado automáticamente.
  const [createdInfo, setCreatedInfo] = useState(null);

  // Dialog de Trazabilidad desde el kanban.
  const [traceabilityRequest, setTraceabilityRequest] = useState(null);

  // Debounce del buscador
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // ============= Carga de datos =============
  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = {};
      if (filterEstado) query.estado = filterEstado;
      if (filterFechaDesde || filterFechaHasta) {
        query.createdAt = {};
        if (filterFechaDesde) query.createdAt.$gte = new Date(filterFechaDesde);
        if (filterFechaHasta) query.createdAt.$lte = new Date(filterFechaHasta);
      }
      const r = await fetchLclRequests({
        limit: 500, // suficiente para el kanban; se filtra por columna en cliente
        offset: 1,
        query,
        sort: { createdAt: -1 },
      });
      setRows(r?.data || []);
    } catch (e) {
      toast.error('Error al cargar solicitudes LCL', e?.message);
    } finally {
      setIsLoading(false);
    }
  }, [filterEstado, filterFechaDesde, filterFechaHasta, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // ============= Helpers =============
  const limpiarFiltros = () => {
    setSearchTerm('');
    setFilterEstado('');
    setFilterFechaDesde('');
    setFilterFechaHasta('');
  };
  const filtrosActivos = Boolean(
    searchTerm || filterEstado || filterFechaDesde || filterFechaHasta,
  );

  // ============= Acciones de fila =============
  const handleAction = useCallback(
    async (action, row) => {
      switch (action) {
        case 'view':
          setDetailRequest(row);
          break;
        case 'traceability':
          setTraceabilityRequest(row);
          break;
        case 'close': {
          if (
            !window.confirm(
              `¿Cerrar la solicitud ${row.codigo || shortId(row._id)}? Se marcará como Aprobada.`,
            )
          )
            return;
          const r = await setEstado(row._id, 'Aprobada');
          if (r?.success) {
            toast.success('Solicitud cerrada', row.codigo || shortId(row._id));
            load();
          } else {
            toast.error('No se pudo cerrar', r?.message);
          }
          break;
        }
        case 'delete': {
          if (!window.confirm('¿Eliminar esta solicitud LCL?')) return;
          const r = await deleteLclRequest(row._id);
          if (r?.success) {
            toast.success('Solicitud eliminada', row.codigo || shortId(row._id));
            load();
          } else {
            toast.error('No se pudo eliminar', r?.message);
          }
          break;
        }
        default:
          break;
      }
    },
    [toast, load],
  );

  // ============= Submit del modal "Nueva solicitud" =============
  const handleNewSubmit = async (payload) => {
    setSubmitting(true);
    try {
      const r = await createLclRequest(payload);
      if (r?.success) {
        toast.success('Solicitud LCL creada', `ID: ${shortId(r.data?._id)}`);
        setOpenNewModal(false);
        setNotified(false);
        setCreatedInfo({
          codigo: r.data?.codigo,
          _id: r.data?._id,
          traceability: r.data?.traceability || null,
        });
        load();
      } else {
        toast.error('No se pudo crear', r?.message || 'Error desconocido');
      }
    } catch (e) {
      toast.error('Error al crear la solicitud', e?.message);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Manda al cliente el correo con el numero de seguimiento. Este es
   * el unico camino por el que sale ese correo: al crear la solicitud
   * ya no se envia solo, para que el operador decida cuando avisar.
   */
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified] = useState(false);

  const handleNotifyClient = useCallback(async () => {
    const id = createdInfo?.traceability?.id;
    if (!id) return;
    setNotifying(true);
    try {
      const r = await notifyRequestCreated(id);
      if (r?.success) {
        setNotified(true);
        toast.success('Cliente informado', r?.message);
      } else {
        toast.error('No se pudo enviar el correo', r?.message);
      }
    } catch (e) {
      toast.error('Error al enviar el correo', e?.message);
    } finally {
      setNotifying(false);
    }
  }, [createdInfo, toast]);

  const handleCopyTraceabilityLink = useCallback(() => {
    if (!createdInfo?.traceability?.numero) return;
    const url = `${window.location.origin}/t/${encodeURIComponent(
      createdInfo.traceability.numero,
    )}`;
    try {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado al portapapeles');
    } catch {
      toast.info(url);
    }
  }, [createdInfo, toast]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* ============= Encabezado ============= */}
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-100 dark:bg-emerald-900/30 p-3 rounded-lg">
              <Ship className="h-8 w-8 text-emerald-600" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-2xl">Solicitudes LCL</CardTitle>
                {tenant ? (
                  <Badge variant="secondary" className="flex items-center gap-1 font-normal">
                    <Building2 className="h-3 w-3" />
                    {tenant.nombre || tenant.slug}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 font-normal text-amber-600 border-amber-300"
                  >
                    <ShieldAlert className="h-3 w-3" />
                    Modo plataforma
                  </Badge>
                )}
              </div>
              <CardDescription>
                Carga marítima consolidada. Consulte el estado de todas las solicitudes.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={isLoading}>
              <RefreshCw className={`mr-1.5 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button onClick={() => setOpenNewModal(true)}>
              <PlusCircle className="mr-2 h-4 w-4" /> Nueva solicitud
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* ============= Filtros ============= */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Buscar por código, cliente, NIT o puerto…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="md:col-span-3">
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Todos los estados</option>
                {ESTADOS.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="date"
                value={filterFechaDesde}
                onChange={(e) => setFilterFechaDesde(e.target.value)}
                className="pl-9"
                title="Fecha desde"
              />
            </div>
            <div className="md:col-span-2 relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="date"
                value={filterFechaHasta}
                onChange={(e) => setFilterFechaHasta(e.target.value)}
                className="pl-9"
                title="Fecha hasta"
              />
            </div>
            <div className="md:col-span-1">
              <Button
                type="button"
                variant="outline"
                onClick={limpiarFiltros}
                disabled={!filtrosActivos}
                className="w-full"
                title="Limpiar filtros"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============= Kanban ============= */}
      <LclOperationKanban
        data={rows}
        onAction={handleAction}
        searchTerm={debouncedSearch}
        loading={isLoading}
        boardRouteByEstado={BOARD_ROUTE_BY_ESTADO}
      />

      {/* ============= Modal "Nueva solicitud" ============= */}
      <Dialog open={openNewModal} onOpenChange={setOpenNewModal}>
        <DialogContent className="w-[80vw] max-w-none max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <LclRequestForm
            onSubmit={handleNewSubmit}
            onCancel={() => setOpenNewModal(false)}
            submitting={submitting}
            submitLabel="Crear solicitud"
            cancelLabel="Cancelar"
            showCancelButton={false}
            hideHeader
            modal
            modalTitle="Crear Solicitud LCL"
            modalSubtitle="Complete todos los campos requeridos"
          />
        </DialogContent>
      </Dialog>

      <LclRequestDetailDialog
        request={detailRequest}
        open={!!detailRequest}
        onOpenChange={(o) => !o && setDetailRequest(null)}
        onAbrirInstrucciones={(r) => {
          setDetailRequest(null);
          setInstruccionesRequest(r);
        }}
      />

      {instruccionesRequest && (
        <LclInstruccionesEmbarqueDialog
          open
          lclRequestId={instruccionesRequest._id}
          onOpenChange={(o) => !o && setInstruccionesRequest(null)}
          onChanged={() => load()}
        />
      )}

      {/* ============= Dialog de Trazabilidad desde kanban ============= */}
      <Dialog
        open={!!traceabilityRequest}
        onOpenChange={(o) => !o && setTraceabilityRequest(null)}
      >
        <DialogContent className="w-[80vw] max-w-none max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 py-4 border-b bg-muted/30 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Hash className="h-5 w-5 text-primary" />
              </div>
              <div className="space-y-0.5">
                <DialogTitle className="text-lg">
                  Trazabilidad ·{' '}
                  {traceabilityRequest?.codigo || shortId(traceabilityRequest?._id)}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Cliente: {formatCliente(traceabilityRequest || {})} ·{' '}
                  {formatRuta(traceabilityRequest || {})}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
            {traceabilityRequest ? (
              <LclTraceabilitySection
                lclRequestId={traceabilityRequest._id}
                clienteSnapshot={traceabilityRequest.clienteSnapshot}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============= Modal "Solicitud creada" ============= */}
      <Dialog open={!!createdInfo} onOpenChange={(o) => !o && setCreatedInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 text-emerald-700 p-2 rounded-lg">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle>Solicitud LCL creada</DialogTitle>
                <DialogDescription>
                  Se genero la trazabilidad. Informa al cliente cuando quieras
                  para enviarle su numero de seguimiento.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/30 p-3 space-y-1">
              <div className="flex items-center gap-2 text-[10px] uppercase font-semibold text-muted-foreground">
                <Hash className="h-3 w-3" />
                Numero de solicitud
              </div>
              <div className="font-mono text-base">{createdInfo?.codigo || '—'}</div>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 space-y-1">
              <div className="flex items-center gap-2 text-[10px] uppercase font-semibold text-emerald-700">
                <Hash className="h-3 w-3" />
                Numero de trazabilidad
              </div>
              {createdInfo?.traceability?.numero ? (
                <div className="font-mono text-base font-semibold text-emerald-900">
                  {createdInfo.traceability.numero}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No se creo (cliente sin correo registrado).
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Comparte este numero con tu cliente para que pueda consultar el
              avance de la operacion. El correo NO se envia solo: usa el boton
              de abajo cuando quieras informarle.
            </p>
          </div>
          <DialogFooter>
            {createdInfo?.traceability?.numero ? (
              <>
                <Button variant="outline" onClick={handleCopyTraceabilityLink}>
                  <Copy className="h-4 w-4 mr-1.5" />
                  Copiar link publico
                </Button>
                <Button
                  onClick={handleNotifyClient}
                  disabled={notifying || notified}
                  title="Enviarle por correo el numero de seguimiento"
                >
                  {notifying ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : notified ? (
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />
                  ) : (
                    <Mail className="h-4 w-4 mr-1.5" />
                  )}
                  {notified ? 'Cliente informado' : 'Informar al cliente'}
                </Button>
              </>
            ) : null}
            <Button onClick={() => setCreatedInfo(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
